import { createContext, useContext } from 'react';

import {
    type DynamicWidgetActionRequest,
    type DynamicWidgetActionStateResolver,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_actions';
import { type DynamicWidgetBuildContext } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';

/**
 * The host provides a build context only when it has proved that the displayed
 * a rich-result renderer is enabled. Missing context is the safe default for v2 conversations,
 * admin traces, tests, and any other surface that cannot establish run
 * identity.
 */
export const DynamicWidgetBuildContextContext =
    createContext<DynamicWidgetBuildContext | null>(null);

export function useDynamicWidgetBuildContext(): DynamicWidgetBuildContext | null {
    return useContext(DynamicWidgetBuildContextContext);
}

export type DynamicWidgetPromptAction = (
    request: string | DynamicWidgetActionRequest,
) => Promise<boolean | void> | boolean | void;

/**
 * Lets widgets send a follow-up prompt on the user's behalf (the trace
 * evidence behind the widgets: 1 in 6 outreach follow-up turns was the user
 * typing back the agent's own proposed action). Null when the host surface
 * cannot send into the conversation (read-only transcripts, admin views) —
 * widgets must render without their action buttons in that case.
 */
export const DynamicWidgetPromptActionContext =
    createContext<DynamicWidgetPromptAction | null>(null);

export function useDynamicWidgetPromptAction(): DynamicWidgetPromptAction | null {
    return useContext(DynamicWidgetPromptActionContext);
}

// Transcript rendering owns committed entries and exposes only the derived
// resolver to widget renderers. Individual widgets never reach into chat
// state or an event store.
export const DynamicWidgetActionStateContext =
    createContext<DynamicWidgetActionStateResolver | null>(null);

export function useDynamicWidgetActionStateResolver(): DynamicWidgetActionStateResolver | null {
    return useContext(DynamicWidgetActionStateContext);
}
